import React, { useEffect, useState } from "react";
import { RequestType } from "../../../models/requestType";
import { FieldGroupRow, FieldGroupRowStatus, RequestAnalysisData } from "../../../models/intake";
import FormConstants from "./DataEntryFieldsConstants";
import { writeFieldToForm } from "./formFieldHelpers";
import DropdownField from "../../../common/formFields/DropdownField";

// DropdownField (the app's shared Bootstrap-styled select, used everywhere
// else in DataEntryDetailsRightPane.tsx) unconditionally reads props.meta.*
// when driven outside redux-form's <Field> wrapper - this component manages
// its own writes via writeFieldToForm instead, so it supplies a static,
// always-valid meta rather than touched/error state.
const STATIC_META = { touched: false, active: false, valid: true, error: undefined };

const DEFAULT_DROPDOWN_VALUE = 0;

export interface RequestTypePurposeFieldProps {
    readonly requestTypes?: readonly RequestType[];
    /** Currently selected Request Type id (read from DATA_ENTRY_FIELDS_FORM). */
    readonly value?: number;
    /** Currently selected Request Purpose id (read from DATA_ENTRY_FIELDS_FORM). */
    readonly purposeValue?: number;
    /**
     * True while the value is still the AI's original suggestion and the user
     * has not overridden it - drives the "AI pre-selected" chip (AC11). Once
     * the user changes either dropdown this component locally treats the row
     * as edited and stops rendering the chip, independent of this prop, so a
     * parent that never re-renders with isAiPreSelected=false still gets
     * correct chip behavior.
     */
    readonly isAiPreSelected: boolean;
    readonly dispatch: Function;
    readonly formName?: string;
    /**
     * Extra, non-form side effects the caller wants to run on a Request Type
     * change (e.g. classic RightPane's sensitive-info reset / barcode DDS
     * trigger). The base DATA_ENTRY_FIELDS_FORM writes are always handled by
     * this component itself via writeFieldToForm - callers should NOT also
     * dispatch a redundant form write here.
     */
    readonly onRequestTypeChange?: (newType: number) => void;
    readonly onRequestPurposeChange?: (newPurpose: number) => void;
}

/**
 * Shared Request Type / Purpose field (Spec #34098 / F1a, AC11). Rendered by
 * both the classic RightPane Details tab and the new Quick Entry row so both
 * views write into - and read from - the same DATA_ENTRY_FIELDS_FORM
 * redux-form instance via the shared writeFieldToForm helper.
 */
const RequestTypePurposeField: React.FC<RequestTypePurposeFieldProps> = (props) => {
    const [edited, setEdited] = useState(false);

    // A fresh AI suggestion (e.g. a new transaction loaded) should re-arm the chip.
    useEffect(() => {
        setEdited(false);
    }, [props.isAiPreSelected]);

    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;
    const selectedType = props.requestTypes?.find(type => type.id === props.value);

    const onChangeType = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newType = parseInt(event.target.value, 10);
        writeFieldToForm(props.dispatch, FormConstants.REQUEST_TYPE, newType, formName);
        writeFieldToForm(props.dispatch, FormConstants.PURPOSE_TYPE, DEFAULT_DROPDOWN_VALUE, formName);
        setEdited(true);
        props.onRequestTypeChange?.(newType);
    };

    const onChangePurpose = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPurpose = parseInt(event.target.value, 10) || DEFAULT_DROPDOWN_VALUE;
        writeFieldToForm(props.dispatch, FormConstants.PURPOSE_TYPE, newPurpose, formName);
        setEdited(true);
        props.onRequestPurposeChange?.(newPurpose);
    };

    const showChip = props.isAiPreSelected && !edited;

    return (
        // Fragment, not a wrapping div: .data-entry's flex-basis: 50% rule
        // (FormField.scss) only applies when it's a DIRECT child of the
        // surrounding flex container (e.g. .details-tab-right-pane in
        // classic RightPane) - an extra wrapper div here breaks that
        // adjacency and collapses the side-by-side layout to stacked full
        // width (visually confirmed via local vs. test-environment
        // screenshot comparison).
        <>
            <div className="data-entry request-type">
                <DropdownField
                    label="What is the request type?"
                    required
                    meta={STATIC_META}
                    options={[
                        { label: "- Select -", value: 0, disabled: true, hidden: true },
                        ...(props.requestTypes?.map(type => ({ label: type.name, value: type.id })) ?? []),
                    ]}
                    onChange={onChangeType}
                    value={props.value ?? DEFAULT_DROPDOWN_VALUE}
                />
            </div>
            <div className="data-entry purpose-type">
                <DropdownField
                    label="What is the purpose of the request?"
                    required
                    meta={STATIC_META}
                    options={[
                        { label: "- Select -", value: 0, disabled: true, hidden: true },
                        ...(selectedType?.requestPurpose.map(purpose => ({ label: purpose.name, value: purpose.id })) ?? []),
                    ]}
                    onChange={onChangePurpose}
                    disabled={!props.value}
                    value={props.purposeValue ?? DEFAULT_DROPDOWN_VALUE}
                />
            </div>
            {showChip ? <span className="qe-chip--ai">AI pre-selected</span> : null}
        </>
    );
};

export default RequestTypePurposeField;

// Exported so DataEntryDetails.tsx can key its Quick Entry renderRowContent
// special-case off the same constant used to build the row (AC11).
export const REQUEST_TYPE_PURPOSE_ROW_ID = "requestTypePurpose";

export interface RequestTypePurposeFormValues {
    readonly requestType?: number;
    readonly requestPurpose?: number;
    readonly requestTypes?: readonly RequestType[];
}

/**
 * Builds the FieldGroupRow for the Request Type/Purpose row from a
 * RequestAnalysisData fixture (Spec #34098 / F1a, AC5/Test 1.1). Request
 * Type/Purpose is always a required field, so an unmapped fixture yields
 * FieldGroupRowStatus.NeedsEntry; a mapped/populated one yields Confirmed
 * (no confidence-dot scoring here - that's F1b-rois's (#34100) domain).
 *
 * formValues is optional (appended, not inserted, to avoid breaking the
 * existing 2/3-arg test call sites) - without it this behaves exactly as
 * before, AI-data-only. Quick Entry passes it so the row also flips to
 * Confirmed once the specialist picks a value manually, not only when the
 * AI extraction itself returns one - the same live-form-value pattern
 * AuthorizationDateField/RequestDateField/CaseNumberField/
 * ClinicResponsibleField's mappers already use.
 */
export function mapRequestTypePurposeToFieldGroupRow(
    data: RequestAnalysisData | undefined,
    badgeNumber: number,
    isEdited: boolean = false,
    formValues?: RequestTypePurposeFormValues,
): FieldGroupRow {
    const hasAiValue = data?.requestTypeMapped !== undefined && data?.requestTypeMapped !== null;
    // BOTH dropdowns must be set - a Type with no Purpose yet is still an
    // incomplete/NeedsEntry row, not Confirmed (the Purpose dropdown is
    // itself disabled until Type is chosen, so Type-only is a real,
    // reachable intermediate state, not a hypothetical).
    const hasFormValue = Boolean(formValues?.requestType) && Boolean(formValues?.requestPurpose);
    const hasMappedValue = hasAiValue || hasFormValue;

    let value: string | null = null;
    if (hasAiValue) {
        value = [data?.requestType, data?.requestPurpose].filter(Boolean).join(" / ") || String(data?.requestTypeMapped);
    } else if (hasFormValue) {
        const selectedType = formValues?.requestTypes?.find(type => type.id === formValues.requestType);
        const selectedPurpose = selectedType?.requestPurpose.find(purpose => purpose.id === formValues?.requestPurpose);
        value = [selectedType?.name, selectedPurpose?.name].filter(Boolean).join(" / ") || null;
    }

    return {
        id: REQUEST_TYPE_PURPOSE_ROW_ID,
        badgeNumber,
        status: hasMappedValue ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.NeedsEntry,
        label: "Request Type & Purpose",
        value,
        isAiPreSelected: hasAiValue && !isEdited,
    };
}
